'use client';
import React from 'react';

import fastStringify from 'fast-json-stable-stringify';
import { DateTime, Interval } from 'luxon';
import NchanSubscriber from 'nchan';
import { filter, head, prop, sortBy, values } from 'ramda';

enum NchanNotificationType {
  clear = 'clear',
  newsdesk = 'newsdesk',
}

interface State {
  readonly notifications: {
    [id in NotificationId]: NewsdeskNotification;
  };
}

const bzNotificationIgnore = 'bzNotificationIgnore';

export enum NewsdeskNotificationType {
  breaking = 'breaking',
  notice = 'notice',
}

type NotificationId = string;

export interface NewsdeskNotification {
  endDate: string; // e.g. '2017-01-18T21:34:26.835Z'
  _id: NotificationId; // e.g. '587fdb2377f9dd00011b0a33'
  notificationType: NewsdeskNotificationType;
  startDate: string; // e.g. '2017-01-18T11:34:26.835Z'
  title: string; // 'BREAKING NEWS: Benzing goes public'
  url: string; // 'https://pro.benzinga.com/dashboard'
}

interface NchanNewsdeskNotification {
  Payload: NewsdeskNotification;
  Type: NchanNotificationType.newsdesk;
}

interface NchanClearNotification {
  Type: NchanNotificationType.clear;
}

type NchanMessage = NchanNewsdeskNotification | NchanClearNotification;

export function useNewsdeskNotifications() {
  // this is stored as a class member instead of on state because (by the nature of being inactive) there's no reason to cause a render by adding a notification that is inactive now (but will be active in the future) since we know that such an operation will not update the DOM and is strictly managerial in nature.
  const inactiveNotifications = React.useRef<{
    [id in NotificationId]: NewsdeskNotification;
  }>({});

  const [state, setState] = React.useState<State>(() => ({
    notifications: {},
  }));

  const socket = React.useMemo(
    () => new NchanSubscriber(`${(window as { env?: { NCHAN_ADDR?: string } }).env?.NCHAN_ADDR ?? ''}/news-alerts/`),
    [],
  );

  const addToActiveList = React.useCallback((notification: NewsdeskNotification) => {
    setState(prevState => ({
      notifications: {
        ...prevState.notifications,
        [notification._id]: notification,
      },
    }));
  }, []);

  const addToInactiveList = React.useCallback((notification: NewsdeskNotification) => {
    inactiveNotifications.current = {
      ...inactiveNotifications.current,
      [notification._id]: notification,
    };
  }, []);

  const removeFromActiveList = React.useCallback((id: NotificationId) => {
    setState(prevState => {
      const { [id]: _, ...notifications } = prevState.notifications;
      return {
        notifications,
      };
    });
  }, []);

  const removeFromInactiveList = React.useCallback((id: NotificationId) => {
    const { [id]: _, ...remainingInactiveNotifications } = inactiveNotifications.current;
    inactiveNotifications.current = remainingInactiveNotifications;
  }, []);

  const notificationExists = React.useCallback(
    (id: NotificationId) => {
      return Boolean(state.notifications[id]) || Boolean(inactiveNotifications[id]);
    },
    [state.notifications],
  );

  const deleteNotification = React.useCallback(
    (id: NotificationId) => {
      if (!notificationExists(id)) {
        return;
      }
      removeFromActiveList(id);
      removeFromInactiveList(id);
    },
    [notificationExists, removeFromActiveList, removeFromInactiveList],
  );

  const activateNotification = React.useCallback(
    (notification: NewsdeskNotification) => {
      if (!notificationExists(notification._id)) {
        return;
      }
      addToActiveList(notification);
      removeFromInactiveList(notification._id);
    },
    [addToActiveList, notificationExists, removeFromInactiveList],
  );

  const addActiveNotification = React.useCallback(
    (notification: NewsdeskNotification) => {
      addToActiveList(notification);

      // schedule removal later when this expires
      const millisecondsFromNow = DateTime.fromISO(notification.endDate).diff(DateTime.local()).milliseconds;
      if (millisecondsFromNow < 0) {
        return;
      }
      window.setTimeout(() => {
        deleteNotification(notification._id);
      }, millisecondsFromNow);
    },
    [addToActiveList, deleteNotification],
  );

  const addInactiveNotification = React.useCallback(
    (notification: NewsdeskNotification) => {
      addToInactiveList(notification);

      // schedule adding to active list for later
      const millisecondsFromNow = DateTime.fromISO(notification.startDate).diff(DateTime.local()).milliseconds;
      if (millisecondsFromNow < 0) {
        return;
      }
      window.setTimeout(() => {
        activateNotification(notification);
      }, millisecondsFromNow);
    },
    [activateNotification, addToInactiveList],
  );

  const getIgnoreList = React.useCallback((): Set<NotificationId> => {
    const ignoreList = window.sessionStorage.getItem(bzNotificationIgnore);
    return ignoreList ? new Set(JSON.parse(ignoreList)) : new Set();
  }, []);

  const notificationIsIgnored = React.useCallback(
    (id: NotificationId) => {
      return getIgnoreList().has(id);
    },
    [getIgnoreList],
  );

  // These two functions take a notification as an argument instead
  // of an id (as the other functinos do) because you may need
  // to check whether a notification is expired when it is received
  // (and therefore before being stored on this.state.notifications
  // where you can lookup by id and get the whole notification).
  const notificationIsExpired = React.useCallback((notification: NewsdeskNotification) => {
    if (!notification) {
      return false;
    }

    const now = DateTime.local();
    const endDate = DateTime.fromISO(notification.endDate);
    return now > endDate;
  }, []);

  // see comment above regarding the arguments
  const notificationIsActive = React.useCallback((notification: NewsdeskNotification) => {
    if (!notification) {
      return false;
    }

    const now = DateTime.local();
    const startDate = DateTime.fromISO(notification.startDate ?? '');
    const endDate = DateTime.fromISO(notification.endDate ?? '');
    const startsNow = now.hasSame(startDate, 'day') && now < endDate;
    return startsNow || Interval.fromDateTimes(startDate, endDate).contains(now);
  }, []);

  const receiveNotification = React.useCallback(
    (notification: NewsdeskNotification) => {
      const { _id: id } = notification;

      if (notificationIsIgnored(id)) {
        return;
      }

      if (notificationIsExpired(notification)) {
        // this will work for updates or new notifications
        deleteNotification(id);
        return;
      }

      if (notificationIsActive(notification)) {
        // this will work if we are updating an existing id or creating a new one
        addActiveNotification(notification);
      } else {
        // this notification is not ready to be displayed (made active), but we want to schedule a re-render when its time comes
        addInactiveNotification(notification);
      }
    },
    [
      addActiveNotification,
      addInactiveNotification,
      deleteNotification,
      notificationIsActive,
      notificationIsExpired,
      notificationIsIgnored,
    ],
  );

  const getEarliest = React.useCallback(
    (notificationType: NewsdeskNotificationType) => {
      const { notifications } = state;
      const allActive = filter(
        notification => notification.notificationType === notificationType && notificationIsActive(notification),
        values(notifications),
      );

      return head(sortBy(prop('startDate'))(allActive));
    },
    [notificationIsActive, state],
  );

  const clear = React.useCallback(() => {
    window.sessionStorage.removeItem(bzNotificationIgnore);
    setState({ notifications: {} });
  }, []);

  const ignoreNotification = React.useCallback(
    (id: NotificationId) => {
      if (!notificationIsIgnored(id)) {
        const ignoreList = getIgnoreList();
        ignoreList.add(id);
        window.sessionStorage.setItem(bzNotificationIgnore, fastStringify([...ignoreList]));
      }

      deleteNotification(id);
    },
    [deleteNotification, getIgnoreList, notificationIsIgnored],
  );

  const onConnect = React.useCallback(() => {
    // console.log('Nchan Connected', event);
  }, []);

  React.useEffect(() => {
    socket.on('connect', onConnect);
    return () => {
      socket.off('connect', onConnect);
    };
  }, [onConnect, socket]);

  const onDisconnect = React.useCallback((event: any) => {
    console.warn('Nchan disconnected', event);
  }, []);

  React.useEffect(() => {
    socket.on('disconnect', onDisconnect);
    return () => {
      socket.off('disconnect', onDisconnect);
    };
  }, [onDisconnect, socket]);

  const onError = React.useCallback((code: any, notification: any) => {
    console.error('Nchan error:', code, notification);
  }, []);

  React.useEffect(() => {
    socket.on('error', onError);
    return () => {
      socket.off('error', onError);
    };
  }, [onError, socket]);

  const onMessage = React.useCallback(
    (messageJSON: any) => {
      // console.log('Nchan Message:', messageJSON);

      const message: NchanMessage = JSON.parse(messageJSON);
      switch (message.Type) {
        case NchanNotificationType.newsdesk:
          receiveNotification(message.Payload);
          break;

        case NchanNotificationType.clear:
          clear();
          break;

        default:
          break;
      }
    },
    [clear, receiveNotification],
  );

  React.useEffect(() => {
    socket.on('message', onMessage);
    return () => {
      socket.off('message', onMessage);
    };
  }, [onMessage, socket]);

  React.useEffect(() => {
    socket.reconnectTimeout = 10000;
    socket.start();
    return () => {
      socket.stop();
    };
  }, [socket]);

  return {
    ignoreNotification,
    notification: getEarliest(NewsdeskNotificationType.breaking) || getEarliest(NewsdeskNotificationType.notice),
  };
}
